import React from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  SimpleGrid,
  Switch,
  Text,
} from '@chakra-ui/react';

const TOGGLES = [
  { key: 'smartCleanup', label: 'Smart detection', description: 'PDF, lists, and paragraphs' },
  { key: 'removeExtraSpaces', label: 'Remove extra spaces', description: 'Collapse runs of spaces' },
  { key: 'trimLines', label: 'Trim each line', description: 'Leading and trailing on every line' },
  { key: 'collapseBlankLines', label: 'Collapse blank lines', description: 'Limit empty line runs' },
  { key: 'mergeBrokenLines', label: 'Merge broken lines', description: 'Join soft-wrapped PDF lines' },
  { key: 'preserveParagraphs', label: 'Preserve paragraphs', description: 'Keep double line breaks' },
  { key: 'preserveLists', label: 'Preserve lists', description: 'Do not merge bullets or numbers' },
  { key: 'flattenToOneLine', label: 'Flatten to one line', description: 'Single paragraph output' },
  { key: 'normalizeQuotes', label: 'Normalize quotes', description: 'Smart quotes → straight' },
  { key: 'removeInvisibleChars', label: 'Remove invisible characters', description: 'Zero-width, BOM, soft hyphen' },
  { key: 'normalizeDashes', label: 'Normalize dashes', description: 'En/em dash to ASCII' },
  { key: 'removeDuplicateLines', label: 'Remove duplicate lines', description: 'First occurrence kept' },
  { key: 'tabsToSpaces', label: 'Tabs → spaces', description: 'Expand tab characters' },
  { key: 'normalizeWhitespace', label: 'Normalize whitespace', description: 'NBSP and special spaces' },
  { key: 'trailingSpaces', label: 'Strip trailing spaces', description: 'End of each line' },
  { key: 'keepOriginalLineBreaks', label: 'Keep original line breaks', description: 'Skip line merging' },
];

const CleanupOptionsPanel = ({ options, onChange }) => (
  <Box
    borderWidth="1px"
    borderColor="gray.100"
    borderRadius="xl"
    bg="gray.50"
    p={{ base: 3, md: 4 }}
  >
    <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
      Options
    </Text>
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacingX={5} spacingY={1}>
      {TOGGLES.map(({ key, label, description }) => (
        <FormControl
          key={key}
          display="flex"
          alignItems="flex-start"
          gap={2}
          borderRadius="md"
          py={0.5}
          px={1}
          mx={-1}
          _focusWithin={{ bg: 'white', shadow: 'sm' }}
        >
          <Switch
            id={`tc-opt-${key}`}
            isChecked={!!options[key]}
            onChange={(e) => onChange(key, e.target.checked)}
            colorScheme="teal"
            mt={0.5}
            flexShrink={0}
            size="sm"
          />
          <Box flex={1} minW={0}>
            <FormLabel htmlFor={`tc-opt-${key}`} mb={0} cursor="pointer" fontWeight="600" fontSize="sm" lineHeight="1.25">
              {label}
            </FormLabel>
            <Text fontSize="xs" color="gray.500" lineHeight="1.25" mt={0}>
              {description}
            </Text>
          </Box>
        </FormControl>
      ))}
    </SimpleGrid>

    {options.collapseBlankLines && (
      <Flex mt={2} align="center" gap={3} flexWrap="wrap">
        <Text fontSize="sm" color="gray.600">
          Max blank lines between blocks
        </Text>
        <Flex gap={2} role="group" aria-label="Maximum consecutive blank lines">
          {[
            { v: 1, label: '1' },
            { v: 2, label: '2' },
          ].map(({ v, label }) => (
            <Button
              key={v}
              size="xs"
              variant={options.maxConsecutiveBlankLines === v ? 'solid' : 'outline'}
              colorScheme="teal"
              onClick={() => onChange('maxConsecutiveBlankLines', v)}
            >
              {label}
            </Button>
          ))}
        </Flex>
      </Flex>
    )}
  </Box>
);

export default CleanupOptionsPanel;
